import { FC, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { guestlistGuestsSelectorCreator } from "Bands/state";
import { IEventGuest, IEventGuestStatus } from "Bands/types";
import { getGuestStatus, usePagination } from "Bands/utils";
import { copy } from "shared/assets";
import { IActionDropdownItem, Pager, Table, TableColumn } from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { BoldText, FlexContainer, Icon, MutedDarkText, MutedText, SmallText } from "shared/styles";
import { IStatus } from "shared/types";
import { formatDateTime, mapUserPermissions } from "shared/utils";
import { IGuestStatusModal } from "./GuestOverview";

const tableCopy = copy.guestlistBlock.guests.create.properties;
const viewCopy = copy.guestlistBlock.guests.view;
const editCopy = copy.guestlistBlock.guests.edit;
const actionsCopy = copy.actions;

interface GuestsTableProps {
  blockId: string;
  fetchGuests: (page: number) => void;
  setShowEditModal: (guest?: IEventGuest) => void;
  setShowDeleteModal: (guest?: IEventGuest) => void;
  setShowStatusModal: (data?: IGuestStatusModal) => void;
  showActions?: boolean;
}

export const GuestsTable: FC<GuestsTableProps> = ({
  blockId,
  fetchGuests,
  setShowEditModal,
  setShowDeleteModal,
  setShowStatusModal,
  showActions,
}) => {
  const pagination = usePagination(API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(blockId));

  const guestsSelector = useMemo(() => {
    const { pages, currentPage } = pagination;

    return guestlistGuestsSelectorCreator(pages?.[currentPage]);
  }, [pagination]);
  const guests = useSelector(guestsSelector);

  const getRowActions = useCallback(
    (id: string) => {
      const findGuest = (id: string): IEventGuest | undefined => {
        return guests.find(guest => guest.id === id);
      };

      const guest = findGuest(id);

      if (!guest) return [];

      const guestPermissions = mapUserPermissions(guest.permissions || []);

      const actions: IActionDropdownItem[] = [];

      if (guestPermissions.EDIT) {
        actions.push({
          label: actionsCopy.edit,
          dataTestId: "edit_guest",
          handleClick: () => setShowEditModal(guest),
        });
      }

      if (guestPermissions.CHANGE_ENTRY_STATUS) {
        if (guest.status !== IEventGuestStatus.approved) {
          actions.push({
            label: editCopy.actions.approve,
            dataTestId: "approve_guest",
            handleClick: () =>
              setShowStatusModal({
                guest,
                status: IEventGuestStatus.approved,
              }),
          });
        }

        if (guest.status !== IEventGuestStatus.pending) {
          actions.push({
            label: editCopy.actions.setPending,
            dataTestId: "set_pending_guest",
            handleClick: () =>
              setShowStatusModal({
                guest,
                status: IEventGuestStatus.pending,
              }),
          });
        }

        if (guest.status !== IEventGuestStatus.denied) {
          actions.push({
            label: editCopy.actions.deny,
            dataTestId: "deny_guest",
            handleClick: () =>
              setShowStatusModal({
                guest,
                status: IEventGuestStatus.denied,
              }),
          });
        }
      }

      if (guestPermissions.DELETE) {
        actions.push({
          label: actionsCopy.delete,
          dataTestId: "delete_guest",
          handleClick: () => setShowDeleteModal(guest),
          status: IStatus.error,
        });
      }

      return actions;
    },
    [guests, setShowDeleteModal, setShowEditModal, setShowStatusModal]
  );

  return (
    <>
      <Table
        data={guests}
        columns={columns}
        getRowActions={getRowActions}
        emptyCopy={viewCopy.messages.empty}
        dataTestId="guestlist_guests_table"
        options={{
          showActions,
          verticalAlignCells: "top",
        }}
      />
      <Pager {...pagination} onPageChange={fetchGuests} />
    </>
  );
};

const columns: TableColumn[] = [
  {
    Header: "Name",
    accessor: "firstname",
    Cell: ({ row }) => {
      const guest = row.original as IEventGuest;

      return (
        <Spacer size="xxs">
          <BoldText>
            {guest.firstname} {guest.lastname}
          </BoldText>
          {guest.source && <SmallText as={MutedText}>{guest.source}</SmallText>}
        </Spacer>
      );
    },
  },
  {
    Header: tableCopy.ticketAndPassTypes,
    accessor: "ticketAndPassTypes",
    Cell: ({ row }) => {
      const guest = row.original as IEventGuest;
      const { nbGuests, ticketAndPassTypes } = guest;

      return (
        <ul>
          {ticketAndPassTypes.map(({ label }) => (
            <li key={label}>
              {nbGuests} <MutedDarkText>×</MutedDarkText> {label}
            </li>
          ))}
        </ul>
      );
    },
  },
  {
    Header: tableCopy.status.title,
    accessor: "status",
    Cell: ({ value }) => {
      const { icon, iconColor, label } = getGuestStatus(value);

      return (
        <FlexContainer flexWrap="nowrap">
          <Icon as={icon} color={iconColor} />
          {label}
        </FlexContainer>
      );
    },
  },
  {
    Header: tableCopy.addedOn,
    accessor: "createdAt",
    Cell: ({ row }) => {
      const guest = row.original as IEventGuest;
      const { firstname, lastname } = guest.addedBy;

      return (
        <Spacer size="xxs">
          <span>{formatDateTime(guest.createdAt)}</span>
          {firstname && (
            <SmallText as={MutedText}>
              by {firstname} {lastname}
            </SmallText>
          )}
        </Spacer>
      );
    },
  },
];
